import { useCallback, useEffect, useState } from "react";
import { Fingerprint, Loader2, Plus, Ban, Copy, Check, ShieldCheck } from "lucide-react";
import { Panel } from "./ui";
import { nexsysApi } from "./api";

const fmt = (t) => (t || "").slice(0, 16).replace("T", " ");
const STATUS_CLS = {
  ACTIVE: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  PENDING: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  REVOKED: "text-white/40 border-white/15 bg-white/[0.04]",
  FAILED: "text-rose-300 border-rose-400/40 bg-rose-400/10",
};

export function IdentityBindings() {
  const [data, setData] = useState(null);
  const [ext, setExt] = useState("");
  const [provider, setProvider] = useState("claude");
  const [busy, setBusy] = useState(false);
  const [busyRevoke, setBusyRevoke] = useState(null);
  const [pendingCode, setPendingCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    nexsysApi.myBindings().then(setData).catch(() => setErr("Could not load your connector identities"));
  }, []);
  useEffect(load, [load]);

  const create = async () => {
    if (!ext.trim()) return;
    setBusy(true);
    setErr("");
    try {
      const r = await nexsysApi.createMyBinding(ext.trim(), provider);
      setPendingCode(r);
      setExt("");
      load();
    } catch (e) {
      setErr(String(e?.message || "").includes("409") ? "That identity is already bound." : "Binding failed.");
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    setBusyRevoke(id);
    try { await nexsysApi.revokeMyBinding(id); load(); } catch { /* noop */ }
    finally { setBusyRevoke(null); }
  };

  const bindings = data?.bindings || [];
  return (
    <Panel title="My Connector Identities" subtitle="Bind the identity you use in Claude / ChatGPT to YOUR NEXSYS principal — identity only, never new authority" testid="identity-bindings-panel">
      <div className="space-y-3 p-4">
        <div className="flex flex-wrap gap-2">
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="rounded-xl border border-white/10 bg-[#0a0f1a] px-2.5 py-2 font-mono text-[11px] text-cyan-200 outline-none focus:border-cyan-400/40"
            data-testid="binding-provider-select"
          >
            {(data?.providers || ["claude", "openai", "generic-api"]).map((p) => (
              <option key={p} value={p}>{p === "openai" ? "OpenAI / ChatGPT" : p === "claude" ? "Anthropic Claude" : "Generic API"}</option>
            ))}
          </select>
          <input
            value={ext}
            onChange={(e) => setExt(e.target.value)}
            placeholder="External identity (e.g. your Claude email or client user id)"
            className="min-w-[220px] flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[12px] text-white/85 outline-none placeholder:text-white/25 focus:border-cyan-400/40"
            data-testid="binding-ext-input"
          />
          <button
            onClick={create}
            disabled={busy || !ext.trim()}
            className="flex items-center gap-1.5 rounded-xl border border-cyan-400/40 bg-cyan-400/10 px-3.5 py-2 text-[12px] font-medium text-cyan-300 hover:bg-cyan-400/20 disabled:opacity-40"
            data-testid="binding-create-btn"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Bind identity
          </button>
        </div>
        {err && <div className="font-mono text-[10px] text-rose-300" data-testid="binding-error">{err}</div>}
        {pendingCode && (
          <div className="rounded-xl border border-amber-400/30 bg-amber-400/[0.06] p-3" data-testid="binding-verification-code">
            <div className="font-mono text-[10px] tracking-widest text-amber-300/80">VERIFICATION CODE — SHOWN ONLY ONCE · 24H · 3 ATTEMPTS</div>
            <div className="mt-1.5 flex items-center gap-2">
              <code className="rounded bg-black/40 px-3 py-1 font-mono text-lg tracking-[0.3em] text-amber-200">{pendingCode.verification_code}</code>
              <button
                onClick={() => { navigator.clipboard?.writeText(pendingCode.verification_code); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                className="rounded-lg border border-white/10 p-1.5 text-white/60 hover:text-white" data-testid="binding-copy-code-btn"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
            <div className="mt-1.5 font-mono text-[9.5px] leading-relaxed text-white/40">
              In your AI client, say: “Use nexsys_interact with user <span className="text-white/70">{pendingCode.external_identity}</span> and
              verification_code <span className="text-white/70">{pendingCode.verification_code}</span>.” The binding turns ACTIVE once verified through the connector.
            </div>
          </div>
        )}
        <div className="nx-scroll max-h-[260px] space-y-2 overflow-y-auto">
          {bindings.length === 0 && <div className="py-3 text-center font-mono text-[11px] text-white/35" data-testid="bindings-empty">No connector identities yet.</div>}
          {bindings.map((b) => {
            const st = (b.status || "ACTIVE").toUpperCase();
            return (
              <div key={b.id} className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid="my-binding-row">
                {st === "ACTIVE" ? <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-300" /> : <Fingerprint className="h-3.5 w-3.5 shrink-0 text-white/40" />}
                <div className="min-w-0 flex-1 font-mono text-[10.5px] text-white/60">
                  <span className="truncate text-white/85">{b.external_identity}</span>
                  <span className="ml-2 text-[9px] uppercase tracking-widest text-cyan-300/70">{b.provider}</span>
                  <div className="text-[9px] text-white/30">
                    {b.bound_via || "session"} · bound {fmt(b.bound_at)} · last validated {b.last_validated ? fmt(b.last_validated) : "never"}
                    {b.failed_reason ? ` · ${b.failed_reason}` : ""}
                  </div>
                </div>
                <span className={`rounded-full border px-2 py-0.5 font-mono text-[9px] tracking-widest ${STATUS_CLS[st] || STATUS_CLS.ACTIVE}`} data-testid="my-binding-status">{st}</span>
                {st !== "REVOKED" && (
                  <button
                    onClick={() => revoke(b.id)}
                    disabled={busyRevoke === b.id}
                    title="Revoke — blocks future access, keeps history"
                    className="rounded-lg border border-white/10 p-1.5 text-white/50 hover:border-rose-400/40 hover:text-rose-300 disabled:opacity-40"
                    data-testid={`my-binding-revoke-${b.id}`}
                  >
                    {busyRevoke === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Panel>
  );
}

export function ConflictAlert() {
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let alive = true;
    const poll = () =>
      nexsysApi.conflictsSummary()
        .then((s) => { if (alive) { setSummary(s); setIsAdmin(true); } })
        .catch(() => { if (alive) setIsAdmin(false); });
    poll();
    const t = setInterval(poll, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!isAdmin || !summary) return null;
  const fresh = summary.new_count > 0;

  const review = async () => {
    setOpen(false);
    try { await nexsysApi.conflictsSeen(); } catch { /* noop */ }
    setSummary((s) => (s ? { ...s, new_count: 0 } : s));
    window.__nexsysSetView?.("gateway");
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        title="Reconciliation conflicts — any client's context vs governed state"
        className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${
          fresh ? "border-rose-400/50 bg-rose-400/10 text-rose-300" : "border-white/10 bg-white/[0.03] text-white/60 hover:text-white"
        }`}
        data-testid="conflict-alert-btn"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`h-4 w-4 ${fresh ? "nx-pulse" : ""}`}>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4" /><path d="M12 17h.01" />
        </svg>
        {fresh && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white" data-testid="conflict-alert-badge">
            {summary.new_count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-50 w-[340px] rounded-xl border border-white/10 bg-[#0a0f1a] p-3 shadow-2xl" data-testid="conflict-alert-dropdown">
          <div className="font-mono text-[9.5px] tracking-[0.25em] text-rose-300/80">
            RECONCILIATION CONFLICTS · {summary.open_count} OPEN{fresh ? ` · ${summary.new_count} NEW` : ""}
          </div>
          <div className="mt-2 max-h-[220px] space-y-1.5 overflow-y-auto">
            {(summary.latest || []).length === 0 && <div className="py-2 text-center font-mono text-[10px] text-emerald-300/60">No open conflicts.</div>}
            {(summary.latest || []).map((c) => (
              <div key={c.id} className="rounded-lg border border-rose-400/15 bg-rose-500/[0.05] px-2.5 py-1.5 font-mono text-[9.5px]" data-testid="conflict-alert-item">
                <div className="truncate text-rose-200/90">{c.content || c.kind}</div>
                <div className="text-[8.5px] text-white/35">{c.principal} · src {c.source || "—"} · {fmt(c.at)}</div>
              </div>
            ))}
          </div>
          <button
            onClick={review}
            className="mt-2 w-full rounded-lg border border-cyan-400/40 bg-cyan-400/10 py-1.5 font-mono text-[10px] text-cyan-300 hover:bg-cyan-400/20"
            data-testid="conflict-alert-review-btn"
          >
            Review in Gateway Console →
          </button>
        </div>
      )}
    </div>
  );
}
